import { ConsentPermissionDescriptor, UserPiiPermissions } from '@/types'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import httpService from './httpService'

const partnersPath = '/user/partners'

export type PartnerPermissionsResponse = {
  partnerId: number
  partnerName: string
  /** The fixed-field form older servers report grants in. Read only when `grants` is absent. */
  permissions: UserPiiPermissions
  permissionDescriptors: ConsentPermissionDescriptor[]
  connectedSince?: string
  lastUpdated?: string
  /** Every key this partner asks for, and whether it is granted. Absent on older servers. */
  grants?: Record<string, boolean> | null
  /** Required keys not granted — the partner will ask for them on the member's next connect. */
  missingRequired?: string[]
}

type ResultPartnerPermissionsResponse = {
  status: string
  results: PartnerPermissionsResponse[]
  errors: Array<{ code: string; message: string }>
}

type ResultString = {
  status: string
  results: string[]
  errors: Array<{ code: string; message: string }>
}

/**
 * These endpoints report a refusal as a 200 with `status: FAILURE` — refusing to revoke a required
 * key is one. Turned into a rejection here so callers' `onError` sees it instead of a false success.
 */
const failOnFailure = (res: ResultString): ResultString => {
  if (res?.status === 'FAILURE') {
    throw new Error(res.errors?.[0]?.message || 'Request failed')
  }
  return res
}

export const PARTNER_CONNECTIONS_KEY = 'partnerConnections'

export const usePartnerConnections = () => {
  return useQuery([PARTNER_CONNECTIONS_KEY], async () => {
    const res = await httpService.Get<ResultPartnerPermissionsResponse>(partnersPath)
    return res.results
  })
}

export const useUpdatePartnerPermissions = () => {
  const queryClient = useQueryClient()
  return useMutation(
    async ({
      partnerId,
      permissions,
      grants,
    }: {
      partnerId: number
      /** Legacy fixed-field form, for an older server. */
      permissions: UserPiiPermissions
      /** Key-based form; a current server uses it and ignores `permissions`. */
      grants?: Record<string, boolean>
    }) => {
      const res = await httpService.Post<ResultString>(`${partnersPath}/${partnerId}/permissions`, {
        permissions,
        ...(grants ? { grants } : {}),
      })
      return failOnFailure(res)
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries([PARTNER_CONNECTIONS_KEY])
      },
    }
  )
}

/**
 * Revoke access. With no `keys`, the partner is disconnected entirely; with `keys`, only those are
 * revoked (the server refuses a required key — the member disconnects to remove one of those).
 * `permissions` mirrors `keys` in the fixed-field form for an older server, which would otherwise
 * read a body it does not understand as no body at all — and disconnect.
 */
export const useRevokePartnerPermissions = () => {
  const queryClient = useQueryClient()
  return useMutation(
    async ({
      partnerId,
      keys,
      permissions,
    }: {
      partnerId: number
      keys?: string[]
      permissions?: UserPiiPermissions
    }) => {
      const body = keys ? { keys, ...(permissions ? { permissions } : {}) } : undefined
      const res = await httpService.Delete<ResultString>(
        `${partnersPath}/${partnerId}/permissions`,
        body ? { data: body } : {}
      )
      return failOnFailure(res)
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries([PARTNER_CONNECTIONS_KEY])
      },
    }
  )
}
